'use strict';

// Хук маски телефона: полностью контролируемый, единственный источник
// правды — value-проп. Внутреннего текстового стейта нет, поэтому рассинхрон
// с родителем и пинг-понг echo невозможны по построению: наружу уходит только
// результат пользовательского ввода и выбора страны.
//
// Приоритет страны: цифры value -> phoneCode-проп -> сохранённая -> гео -> null.
// Геолокация (ymaps) асинхронна и на первом маунте её обычно нет — отдельный
// стейт дожидается её появления и пересчитывает страну (раньше страна
// считалась один раз на маунт, отсюда пустой флаг при первом открытии).

const countries = () => window.countryMasks || [];

// Только цифры из любого ввода.
export const extractDigits = (rawPhone) => String(rawPhone === null || rawPhone === undefined ? '' : rawPhone).replace(/\D/g, '');

// Канонический вид: "" для пустого, иначе всегда с ведущим "+".
export const toClearedPhone = (digits) => (digits.length > 0 ? `+${digits}` : '');

// Поиск страны по введённым цифрам: самый длинный совпавший префикс.
// Не нашлась — null (нейтральный флаг, без маски).
export const findCountryByDigits = (digits) => {
    if (!digits) return null;

    const matches = countries().filter((country) => {
        const prefix = extractDigits(country.phone);
        return prefix.length > 0 && digits.startsWith(prefix);
    });
    matches.sort((a, b) => extractDigits(b.phone).length - extractDigits(a.phone).length);

    return matches[0] || null;
};

export const findCountryByCode = (code) => {
    if (!code) return null;
    return countries().find((country) => country.code.toLowerCase() === String(code).toLowerCase()) || null;
};

export const findCountryByName = (name) => {
    if (!name) return null;
    return countries().find((country) => country.name.toLowerCase() === String(name).toLowerCase()) || null;
};

export const getCountryMasks = (country) => (
    country
        ? [country.mask, ...(country.additional_masks || [])].filter(Boolean)
        : []
);

export const getSuitableMask = (phone, masks) => {
    const sortedMasks = [...masks];
    const phoneLength = phone.replace(/[^\d#]/g, '').length;
    sortedMasks.sort((a, b) => a.replace(/[^\d#]/g, '').length - b.replace(/[^\d#]/g, '').length);
    const foundIndex = sortedMasks.findIndex((mask) => phoneLength <= mask.replace(/[^\d#]/g, '').length);

    if (foundIndex === -1) {
        return sortedMasks[sortedMasks.length - 1];
    }

    return sortedMasks[foundIndex];
};

// Ёмкость маски — количество цифр и "#" (то есть вместе с кодом страны),
// ровно так же длину номера проверяет Phone::checkPhoneComplete на бэкенде.
export const countMaskDigits = (mask) => mask.replace(/[^\d#]/g, '').length;

// Обрезает цифры до ёмкости подходящей маски страны: лишние цифры не должны
// попадать в value (иначе они не видны в инпуте, но уходят на сервер).
export const limitPhoneDigits = (digits, country = findCountryByDigits(digits)) => {
    if (!digits || !country) return digits;

    const masks = getCountryMasks(country);
    if (masks.length === 0) return digits;

    const mask = getSuitableMask(toClearedPhone(digits), masks);
    return digits.slice(0, countMaskDigits(mask));
};

// Форматирование цифр по маске. Пустые цифры — всегда пустая строка
// (никакого висящего "+" в пустом инпуте).
export const applyPhoneMask = (digits, mask) => {
    if (!digits) return '';

    let i = 0;
    return mask.replace(/(?!\+)./g, function (a) {
        return /[#\d]/.test(a) && i < digits.length ? digits.charAt(i++) : i >= digits.length ? '' : a;
    });
};

export const isMaskComplete = (formatted, mask) => formatted.length === mask.length;

// Разбор сырого ввода: цифры, каноника, детект страны по цифрам, полнота.
// Используется и в хуке, и в обработчике ввода компонента.
export const resolvePhoneInput = (raw) => {
    const rawDigits = extractDigits(raw);
    const country = rawDigits ? findCountryByDigits(rawDigits) : null;
    const digits = limitPhoneDigits(rawDigits, country);
    const cleared = toClearedPhone(digits);

    let complete = false;
    if (digits) {
        const masks = getCountryMasks(country);
        if (masks.length > 0) {
            const mask = getSuitableMask(cleared, masks);
            complete = isMaskComplete(applyPhoneMask(digits, mask), mask);
        } else {
            complete = digits.length >= 7;
        }
    }

    return { digits, cleared, country, complete };
};

const LS_COUNTRY_KEY = 'phoneCountryCode';

const readSavedCountryCode = () => {
    try {
        if (typeof storageGet !== 'function' || !window.countryMasks) return '';
        const saved = storageGet(LS_COUNTRY_KEY);
        if (typeof saved === 'string' && window.countryMasks.some((country) => country.code === saved)) return saved;
    } catch (e) {}
    return '';
};

const saveCountryCode = (code) => {
    try {
        if (typeof storageSet === 'function' && code) storageSet(LS_COUNTRY_KEY, code);
    } catch (e) {}
};

const readGeoCountryName = () => (
    (window.ymaps && window.ymaps.geolocation && window.ymaps.geolocation.country) || ''
);

// Гео-страна отдельным стейтом: ymaps подгружается асинхронно и на первом
// маунте её обычно нет — дожидаемся появления и пересчитываем.
const useGeoCountryName = () => {
    const [geoName, setGeoName] = React.useState(readGeoCountryName);

    React.useEffect(() => {
        if (readGeoCountryName()) {
            setGeoName(readGeoCountryName());
            return;
        }

        let tries = 0;
        const timer = setInterval(() => {
            const name = readGeoCountryName();
            tries += 1;
            if (name || tries > 20) {
                if (name) setGeoName(name);
                clearInterval(timer);
            }
        }, 400);
        const onLoad = () => {
            const name = readGeoCountryName();
            if (name) {
                setGeoName(name);
                clearInterval(timer);
            }
        };
        window.addEventListener('load', onLoad);

        return () => {
            clearInterval(timer);
            window.removeEventListener('load', onLoad);
        };
    }, []);

    return geoName;
};

export default function usePhoneMask({ value = '', phoneCode = undefined }) {
    const geoName = useGeoCountryName();

    const digits = React.useMemo(() => {
        const raw = extractDigits(value);
        return raw ? limitPhoneDigits(raw) : '';
    }, [value]);
    const cleared = React.useMemo(() => toClearedPhone(digits), [digits]);

    // Приоритет: цифры value -> phoneCode-проп -> сохранённая -> гео -> null.
    const country = React.useMemo(() => {
        if (digits) return findCountryByDigits(digits);
        if (phoneCode) return findCountryByCode(phoneCode);

        const saved = readSavedCountryCode();
        if (saved) return findCountryByCode(saved);

        if (geoName) {
            let name = geoName;
            if (window.Site && window.Site.isAviola && name === 'Russia') name = '';
            return name ? findCountryByName(name) : null;
        }

        return null;
    }, [digits, phoneCode, geoName]);

    // Гео-детект кэшируем, чтобы следующий маунт был мгновенным
    // даже до подгрузки ymaps (выбор пользователя в приоритете — он в LS).
    React.useEffect(() => {
        if (country && !digits && !phoneCode && !readSavedCountryCode()) {
            saveCountryCode(country.code);
        }
    }, [country, digits, phoneCode]);

    const masks = React.useMemo(
        () => getCountryMasks(country),
        [country]
    );

    const mask = React.useMemo(() => {
        if (!digits || masks.length === 0) return null;
        return getSuitableMask(cleared, masks);
    }, [digits, cleared, masks]);

    const display = React.useMemo(() => {
        if (!digits) return '';
        if (!mask) return cleared;
        return applyPhoneMask(digits, mask);
    }, [digits, cleared, mask]);

    const isComplete = React.useMemo(() => {
        if (!digits) return false;
        if (mask) return isMaskComplete(display, mask);
        return digits.length >= 7;
    }, [digits, mask, display]);

    return {
        digits,
        cleared,
        display,
        country,
        mask,
        isComplete,
    };
}
